import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  CalendarDays, DoorOpen, Settings2, Clock, BookOpen,
  Plus, Pencil, Trash2, Loader2, Save, X, Check,
  Projector, Wind, LayoutGrid, ChevronDown,
  TrendingUp, TrendingDown, Building2, BookMarked, Zap, BarChart3,
  Search, Download, Upload, FileUp, CheckCircle2, AlertCircle, RefreshCw,
} from 'lucide-react';
import { YearContext } from '../components/Layout/Layout';
import { timetableAPI, schoolConfigAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// ── Constants ─────────────────────────────────────────────────
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat'];
const DAY_LABELS = { Mon:'Monday', Tue:'Tuesday', Wed:'Wednesday', Thu:'Thursday', Fri:'Friday', Sat:'Saturday' };
const ROOM_TYPES = ['classroom','lab','library','hall','gym','art_room','music_room','other'];
const ROOM_TYPE_LABELS = {
  classroom:'Classroom', lab:'Lab', library:'Library', hall:'Hall',
  gym:'Gym', art_room:'Art Room', music_room:'Music Room', other:'Other',
};
const ROOM_TYPE_COLORS = {
  classroom:'bg-blue-100 text-blue-700', lab:'bg-purple-100 text-purple-700',
  library:'bg-amber-100 text-amber-700', hall:'bg-slate-100 text-slate-700',
  gym:'bg-emerald-100 text-emerald-700', art_room:'bg-pink-100 text-pink-700',
  music_room:'bg-indigo-100 text-indigo-700', other:'bg-slate-100 text-slate-600',
};
const PERIOD_TYPES = ['regular','break','lunch','assembly','activity','free'];
const PERIOD_TYPE_LABELS = {
  regular:'Regular', break:'Break', lunch:'Lunch', assembly:'Assembly',
  activity:'Activity', free:'Free Period',
};
const PERIOD_TYPE_COLORS = {
  regular:'bg-blue-100 text-blue-700', break:'bg-amber-100 text-amber-700',
  lunch:'bg-orange-100 text-orange-700', assembly:'bg-purple-100 text-purple-700',
  activity:'bg-emerald-100 text-emerald-700', free:'bg-slate-100 text-slate-500',
};
const SUBJECT_PALETTE = [
  ['bg-blue-100   text-blue-800   border-blue-200',   '#dbeafe'],
  ['bg-emerald-100 text-emerald-800 border-emerald-200','#d1fae5'],
  ['bg-purple-100 text-purple-800 border-purple-200', '#f3e8ff'],
  ['bg-amber-100  text-amber-800  border-amber-100',  '#fef3c7'],
  ['bg-rose-100   text-rose-800   border-rose-200',   '#ffe4e6'],
  ['bg-cyan-100   text-cyan-800   border-cyan-200',   '#cffafe'],
  ['bg-indigo-100 text-indigo-800 border-indigo-200', '#e0e7ff'],
  ['bg-orange-100 text-orange-800 border-orange-200', '#ffedd5'],
  ['bg-teal-100   text-teal-800   border-teal-200',   '#ccfbf1'],
  ['bg-pink-100   text-pink-800   border-pink-200',   '#fce7f3'],
  ['bg-lime-100   text-lime-800   border-lime-200',   '#ecfccb'],
  ['bg-sky-100    text-sky-800    border-sky-200',    '#e0f2fe'],
];

function subjectClass(name) {
  if (!name) return '';
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0x7fffffff;
  return SUBJECT_PALETTE[h % SUBJECT_PALETTE.length][0];
}

// ── Helpers ───────────────────────────────────────────────────
function addMins(timeStr, mins) {
  const [h, m] = (timeStr || '07:45').split(':').map(Number);
  const t = h * 60 + m + mins;
  return `${String(Math.floor(t/60)%24).padStart(2,'0')}:${String(t%60).padStart(2,'0')}`;
}
function diffMins(s, e) {
  const [sh,sm] = s.split(':').map(Number);
  const [eh,em] = e.split(':').map(Number);
  return (eh*60+em)-(sh*60+sm);
}

// ── RoomModal ─────────────────────────────────────────────────
function RoomModal({ room, yearId, onSave, onClose }) {
  const isEdit = !!room?.id;
  const [form, setForm] = useState({
    name: room?.name||'', room_code: room?.room_code||'',
    room_type: room?.room_type||'classroom', capacity: room?.capacity||30,
    floor: room?.floor||1, building: room?.building||'',
    has_projector: !!room?.has_projector, has_ac: !!room?.has_ac,
    is_active: room?.is_active !== 0, notes: room?.notes||'',
  });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Room name required');
    setSaving(true);
    try {
      isEdit ? await timetableAPI.updateRoom(room.id, {...form, academicYearId:yearId})
             : await timetableAPI.createRoom({...form, academicYearId:yearId});
      toast.success(isEdit ? 'Room updated' : 'Room created');
      onSave();
    } catch(e) { toast.error(e.response?.data?.error||'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-display font-bold text-slate-800">{isEdit?'Edit Room':'Add Room'}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={18}/></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Room Name *</label>
              <input value={form.name} onChange={e=>set('name',e.target.value)} required
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" placeholder="e.g. Class I-A"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Room Code</label>
              <input value={form.room_code} onChange={e=>set('room_code',e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" placeholder="e.g. R101"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
              <select value={form.room_type} onChange={e=>set('room_type',e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400">
                {ROOM_TYPES.map(t=><option key={t} value={t}>{ROOM_TYPE_LABELS[t]}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Capacity</label>
              <input type="number" min="1" max="200" value={form.capacity} onChange={e=>set('capacity',parseInt(e.target.value))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"/>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Floor</label>
              <input type="number" min="0" max="10" value={form.floor} onChange={e=>set('floor',parseInt(e.target.value))}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"/>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Building / Block</label>
              <input value={form.building} onChange={e=>set('building',e.target.value)}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" placeholder="e.g. Main Block"/>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            {[['has_projector','Projector'],['has_ac','Air Conditioning'],['is_active','Active']].map(([k,l])=>(
              <label key={k} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={form[k]} onChange={e=>set(k,e.target.checked)} className="rounded"/>
                {l}
              </label>
            ))}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e=>set('notes',e.target.value)} rows={2}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"/>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200">Cancel</button>
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 disabled:opacity-50">
              {saving?<Loader2 size={14} className="animate-spin"/>:<Save size={14}/>}
              {isEdit?'Update':'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Rooms tab ─────────────────────────────────────────────────
function RoomsTab({ yearId, isAdmin }) {
  const [rooms,setRooms]=useState([]); const [loading,setLoading]=useState(true);
  const [modal,setModal]=useState(null); const [deleting,setDeleting]=useState(null);

  const load = useCallback(async()=>{
    setLoading(true);
    try { const r=await timetableAPI.getRooms({academicYearId:yearId}); setRooms(r.data.data||[]); }
    catch { toast.error('Failed to load rooms'); } finally { setLoading(false); }
  },[yearId]);
  useEffect(()=>{load();},[load]);

  const handleDelete = async(id)=>{
    if(!window.confirm('Delete this room?')) return;
    setDeleting(id);
    try { await timetableAPI.deleteRoom(id); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); } finally { setDeleting(null); }
  };

  const byFloor = rooms.reduce((acc,r)=>{ const f=r.floor??1; if(!acc[f])acc[f]=[]; acc[f].push(r); return acc; },{});

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{rooms.length} room{rooms.length!==1?'s':''} configured</p>
        {isAdmin&&<button onClick={()=>setModal('add')} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700"><Plus size={15}/>Add Room</button>}
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-primary-500"/></div>
      : rooms.length===0 ? (
        <div className="text-center py-16 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
          <DoorOpen size={40} className="mx-auto text-slate-300 mb-3"/>
          <p className="text-slate-600 font-medium">No rooms yet</p>
          {isAdmin&&<p className="text-slate-400 text-sm mt-1">Click "Add Room" to create your first room.</p>}
        </div>
      ) : Object.entries(byFloor).sort(([a],[b])=>a-b).map(([floor,floorRooms])=>(
        <div key={floor} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b"><h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Floor {floor}</h4></div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 font-semibold bg-slate-50/60">
              <tr>
                <th className="px-4 py-2.5 text-left">Room</th>
                <th className="px-3 py-2.5 text-left">Type</th>
                <th className="px-3 py-2.5 text-right">Cap.</th>
                <th className="px-3 py-2.5 text-left">Building</th>
                <th className="px-3 py-2.5 text-center">Facilities</th>
                <th className="px-3 py-2.5 text-center">Status</th>
                {isAdmin&&<th className="px-4 py-2.5 text-right"/>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {floorRooms.map(room=>(
                <tr key={room.id} className={`hover:bg-slate-50 ${!room.is_active?'opacity-50':''}`}>
                  <td className="px-4 py-2.5"><div className="font-medium text-slate-700">{room.name}</div>{room.room_code&&<div className="text-xs text-slate-400">{room.room_code}</div>}</td>
                  <td className="px-3 py-2.5"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${ROOM_TYPE_COLORS[room.room_type]||''}`}>{ROOM_TYPE_LABELS[room.room_type]||room.room_type}</span></td>
                  <td className="px-3 py-2.5 text-right text-slate-600">{room.capacity}</td>
                  <td className="px-3 py-2.5 text-slate-500 text-xs">{room.building||'—'}</td>
                  <td className="px-3 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      {room.has_projector&&<span title="Projector" className="text-primary-500"><Projector size={14}/></span>}
                      {room.has_ac&&<span title="AC" className="text-sky-500"><Wind size={14}/></span>}
                      {!room.has_projector&&!room.has_ac&&<span className="text-slate-300 text-xs">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-center"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${room.is_active?'bg-emerald-100 text-emerald-700':'bg-slate-100 text-slate-500'}`}>{room.is_active?'Active':'Inactive'}</span></td>
                  {isAdmin&&<td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={()=>setModal(room)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-primary-600"><Pencil size={14}/></button>
                      <button onClick={()=>handleDelete(room.id)} disabled={deleting===room.id} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500">
                        {deleting===room.id?<Loader2 size={14} className="animate-spin"/>:<Trash2 size={14}/>}
                      </button>
                    </div>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {modal&&<RoomModal room={modal==='add'?null:modal} yearId={yearId} onSave={()=>{setModal(null);load();}} onClose={()=>setModal(null)}/>}
    </div>
  );
}

// ── Config tab ────────────────────────────────────────────────
function ConfigTab({ yearId, isAdmin }) {
  const [cfg,setCfg]=useState(null); const [loading,setLoading]=useState(true); const [saving,setSaving]=useState(false);
  useEffect(()=>{
    timetableAPI.getConfig({academicYearId:yearId}).then(r=>setCfg(r.data)).catch(()=>toast.error('Failed')).finally(()=>setLoading(false));
  },[yearId]);
  const set=(k,v)=>setCfg(c=>({...c,[k]:v}));
  const toggleDay=d=>{ const days=cfg.working_days||[]; set('working_days',days.includes(d)?days.filter(x=>x!==d):[...days,d]); };
  const inp="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400";
  const F=({label,children})=><div><label className="block text-xs font-medium text-slate-600 mb-1.5">{label}</label>{children}</div>;
  const periodOpts=[{value:'',label:'None'},...[1,2,3,4,5,6,7,8,9,10].map(n=>({value:n,label:`After Period ${n}`}))];

  if(loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-primary-500"/></div>;
  if(!cfg) return null;

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-display font-semibold text-slate-800 mb-4">Working Days</h3>
        <div className="flex flex-wrap gap-2">
          {DAYS.map(d=>{ const active=(cfg.working_days||[]).includes(d); return (
            <button key={d} type="button" onClick={()=>isAdmin&&toggleDay(d)} disabled={!isAdmin}
              className={`px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${active?'bg-primary-600 text-white border-primary-600':'bg-white text-slate-500 border-slate-200 hover:border-primary-300'} ${!isAdmin?'cursor-default':''}`}>
              {DAY_LABELS[d]}
            </button>
          );})}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-display font-semibold text-slate-800 mb-4">School Hours</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <F label="Start Time"><input type="time" value={(cfg.school_start_time||'07:45').slice(0,5)} disabled={!isAdmin} onChange={e=>set('school_start_time',e.target.value+':00')} className={inp}/></F>
          <F label="End Time"><input type="time" value={(cfg.school_end_time||'14:45').slice(0,5)} disabled={!isAdmin} onChange={e=>set('school_end_time',e.target.value+':00')} className={inp}/></F>
          <F label="Periods per Day"><input type="number" min="1" max="12" value={cfg.periods_per_day||8} disabled={!isAdmin} onChange={e=>set('periods_per_day',parseInt(e.target.value))} className={inp}/></F>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-display font-semibold text-slate-800 mb-4">Breaks &amp; Lunch</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <F label="Break After"><select value={cfg.break_after_period||''} disabled={!isAdmin} onChange={e=>set('break_after_period',e.target.value?parseInt(e.target.value):null)} className={inp}>{periodOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select></F>
          <F label="Break Duration (min)"><input type="number" min="5" max="60" value={cfg.break_duration_min||15} disabled={!isAdmin} onChange={e=>set('break_duration_min',parseInt(e.target.value))} className={inp}/></F>
          <div/>
          <F label="Lunch After"><select value={cfg.lunch_after_period||''} disabled={!isAdmin} onChange={e=>set('lunch_after_period',e.target.value?parseInt(e.target.value):null)} className={inp}>{periodOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select></F>
          <F label="Lunch Duration (min)"><input type="number" min="10" max="90" value={cfg.lunch_duration_min||30} disabled={!isAdmin} onChange={e=>set('lunch_duration_min',parseInt(e.target.value))} className={inp}/></F>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-display font-semibold text-slate-800 mb-4">Assembly &amp; Teacher Rules</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <F label="Assembly Day"><select value={cfg.assembly_day||''} disabled={!isAdmin} onChange={e=>set('assembly_day',e.target.value||null)} className={inp}><option value="">None</option>{DAYS.map(d=><option key={d} value={d}>{DAY_LABELS[d]}</option>)}</select></F>
          <F label="Assembly Duration (min)"><input type="number" min="10" max="60" value={cfg.assembly_duration_min||20} disabled={!isAdmin} onChange={e=>set('assembly_duration_min',parseInt(e.target.value))} className={inp}/></F>
          <div/>
          <F label="Max Periods / Teacher / Day"><input type="number" min="1" max="10" value={cfg.max_periods_per_teacher_per_day||6} disabled={!isAdmin} onChange={e=>set('max_periods_per_teacher_per_day',parseInt(e.target.value))} className={inp}/></F>
          <F label="Min Free Periods / Week"><input type="number" min="0" max="10" value={cfg.min_free_periods_per_week||2} disabled={!isAdmin} onChange={e=>set('min_free_periods_per_week',parseInt(e.target.value))} className={inp}/></F>
          <F label="Allow Double Periods"><div className="flex items-center gap-2 mt-1"><input type="checkbox" checked={!!cfg.allow_double_periods} disabled={!isAdmin} onChange={e=>set('allow_double_periods',e.target.checked?1:0)} className="rounded w-4 h-4"/><span className="text-sm text-slate-600">Enabled</span></div></F>
        </div>
      </div>
      {isAdmin&&<button onClick={async()=>{setSaving(true);try{await timetableAPI.updateConfig({...cfg,academicYearId:yearId});toast.success('Saved');}catch{toast.error('Save failed');}finally{setSaving(false);}}} disabled={saving}
        className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50">
        {saving?<Loader2 size={15} className="animate-spin"/>:<Save size={15}/>}Save Configuration
      </button>}
    </div>
  );
}

// ── Periods tab ───────────────────────────────────────────────
function PeriodsTab({ yearId, isAdmin }) {
  const [periods,setPeriods]=useState([]); const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false); const [cfg,setCfg]=useState(null);

  const load = useCallback(async()=>{
    setLoading(true);
    try {
      const [pr,cr]=await Promise.all([timetableAPI.getPeriods({academicYearId:yearId}),timetableAPI.getConfig({academicYearId:yearId})]);
      setPeriods(pr.data.data||[]); setCfg(cr.data);
    } catch { toast.error('Failed to load periods'); } finally { setLoading(false); }
  },[yearId]);
  useEffect(()=>{load();},[load]);

  const upd=(idx,field,value)=>setPeriods(prev=>prev.map((p,i)=>{
    if(i!==idx) return p;
    const u={...p,[field]:value};
    if(field==='start_time'||field==='end_time'){ const s=field==='start_time'?value:u.start_time; const e=field==='end_time'?value:u.end_time; if(s&&e) u.duration_min=Math.max(0,diffMins(s,e)); }
    return u;
  }));

  const autoGen=()=>{
    if(!cfg) return;
    const st=(cfg.school_start_time||'07:45:00').slice(0,5);
    const n=cfg.periods_per_day||8; const ba=cfg.break_after_period; const bd=cfg.break_duration_min||15;
    const la=cfg.lunch_after_period; const ld=cfg.lunch_duration_min||30; const dur=40;
    let cur=st; const gen=[];
    for(let i=1;i<=n;i++){
      const s=cur,e=addMins(cur,dur);
      gen.push({period_number:i,label:`Period ${i}`,start_time:s,end_time:e,duration_min:dur,period_type:'regular'});
      cur=e;
      if(i===ba) cur=addMins(cur,bd);
      if(i===la) cur=addMins(cur,ld);
    }
    setPeriods(gen); toast.success('Auto-generated — review and save');
  };

  const addRow=()=>{
    const last=periods[periods.length-1];
    const num=(last?.period_number||0)+1; const ns=last?.end_time?.slice(0,5)||'07:45'; const ne=addMins(ns,40);
    setPeriods(p=>[...p,{period_number:num,label:`Period ${num}`,start_time:ns,end_time:ne,duration_min:40,period_type:'regular'}]);
  };

  if(loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-primary-500"/></div>;

  return (
    <div className="space-y-4">
      {isAdmin&&<div className="flex flex-wrap gap-3">
        <button onClick={autoGen} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-xl hover:bg-amber-600"><Clock size={15}/>Auto-Generate from Config</button>
        <button onClick={addRow} className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-200"><Plus size={15}/>Add Row</button>
      </div>}
      {periods.length===0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200"><Clock size={36} className="mx-auto text-slate-300 mb-3"/><p className="text-slate-600 font-medium">No period templates yet</p>{isAdmin&&<p className="text-slate-400 text-sm mt-1">Click "Auto-Generate" to build periods from your school hours config.</p>}</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold">
              <tr><th className="px-3 py-2.5 text-center w-14">No.</th><th className="px-3 py-2.5 text-left">Label</th><th className="px-3 py-2.5 text-center">Start</th><th className="px-3 py-2.5 text-center">End</th><th className="px-3 py-2.5 text-center">Min</th><th className="px-3 py-2.5 text-left">Type</th>{isAdmin&&<th className="px-3 py-2.5 w-10"/>}</tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {periods.map((p,idx)=>(
                <tr key={idx} className={`hover:bg-slate-50 ${p.period_type!=='regular'?'bg-amber-50/40':''}`}>
                  <td className="px-3 py-2 text-center text-xs font-bold text-slate-400">{p.period_number}</td>
                  <td className="px-3 py-2">{isAdmin?<input value={p.label||''} onChange={e=>upd(idx,'label',e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-400"/>:<span>{p.label}</span>}</td>
                  <td className="px-3 py-2 text-center">{isAdmin?<input type="time" value={(p.start_time||'').slice(0,5)} onChange={e=>upd(idx,'start_time',e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-400"/>:<span>{(p.start_time||'').slice(0,5)}</span>}</td>
                  <td className="px-3 py-2 text-center">{isAdmin?<input type="time" value={(p.end_time||'').slice(0,5)} onChange={e=>upd(idx,'end_time',e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-400"/>:<span>{(p.end_time||'').slice(0,5)}</span>}</td>
                  <td className="px-3 py-2 text-center text-slate-500 tabular-nums">{p.duration_min}</td>
                  <td className="px-3 py-2">{isAdmin?<select value={p.period_type} onChange={e=>upd(idx,'period_type',e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-400">{PERIOD_TYPES.map(t=><option key={t} value={t}>{PERIOD_TYPE_LABELS[t]}</option>)}</select>:<span className={`text-xs font-medium px-2 py-0.5 rounded-full ${PERIOD_TYPE_COLORS[p.period_type]}`}>{PERIOD_TYPE_LABELS[p.period_type]||p.period_type}</span>}</td>
                  {isAdmin&&<td className="px-3 py-2 text-center"><button onClick={()=>setPeriods(p=>p.filter((_,i)=>i!==idx))} className="p-1 rounded text-slate-300 hover:text-red-500"><X size={14}/></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isAdmin&&periods.length>0&&<button onClick={async()=>{setSaving(true);try{await timetableAPI.upsertPeriods({academicYearId:yearId,periods});toast.success('Periods saved');load();}catch{toast.error('Save failed');}finally{setSaving(false);}}} disabled={saving}
        className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50">
        {saving?<Loader2 size={15} className="animate-spin"/>:<Save size={15}/>}Save Periods
      </button>}
    </div>
  );
}

// ── Grade config tab ──────────────────────────────────────────
function GradeConfigTab({ yearId, isAdmin }) {
  const [rows,setRows]=useState([]); const [loading,setLoading]=useState(true); const [saving,setSaving]=useState(false);
  const [grades,setGrades]=useState([]); const [subjects,setSubjects]=useState([]);
  const [addForm,setAddForm]=useState(null); const [deleting,setDeleting]=useState(null);

  const load=useCallback(async()=>{
    setLoading(true);
    try {
      const [gcR,grR,subR]=await Promise.all([timetableAPI.getGradeConfig({academicYearId:yearId}),schoolConfigAPI.getGradeOptions(),schoolConfigAPI.getSubjects()]);
      setRows(gcR.data.data||[]);
      setGrades((grR.data||[]).map(g=>typeof g==='string'?g:g.grade||g.name));
      setSubjects((subR.data||[]).filter(s=>s.is_active!==0));
    } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  },[yearId]);
  useEffect(()=>{load();},[load]);

  const upd=(idx,f,v)=>setRows(prev=>prev.map((r,i)=>i===idx?{...r,[f]:v}:r));
  const handleAdd=async(e)=>{
    e.preventDefault(); if(!addForm.grade||!addForm.subject_id) return toast.error('Grade and Subject required');
    setSaving(true);
    try {
      const subj=subjects.find(s=>String(s.id)===String(addForm.subject_id));
      await timetableAPI.upsertGradeConfig({academicYearId:yearId,rows:[{grade:addForm.grade,subject_id:parseInt(addForm.subject_id),subject_name:subj?.name||'',periods_per_week:parseInt(addForm.periods_per_week),can_double:addForm.can_double}]});
      toast.success('Added'); setAddForm(null); load();
    } catch(e){ toast.error(e.response?.data?.error||'Save failed'); } finally{setSaving(false);}
  };
  const handleDelete=async(id)=>{ setDeleting(id); try{await timetableAPI.deleteGradeConfig(id);toast.success('Removed');load();}catch{toast.error('Failed');}finally{setDeleting(null);}};
  const saveAll=async()=>{ setSaving(true); try{await timetableAPI.upsertGradeConfig({academicYearId:yearId,rows});toast.success('Saved');}catch{toast.error('Save failed');}finally{setSaving(false);} };

  const byGrade=rows.reduce((acc,r)=>{if(!acc[r.grade])acc[r.grade]=[];acc[r.grade].push(r);return acc;},{});
  const BLANK={grade:'',subject_id:'',periods_per_week:4,can_double:false};

  if(loading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-primary-500"/></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{rows.length} requirement{rows.length!==1?'s':''}</p>
        {isAdmin&&!addForm&&<button onClick={()=>setAddForm({...BLANK})} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700"><Plus size={15}/>Add Requirement</button>}
      </div>
      {addForm&&(
        <form onSubmit={handleAdd} className="bg-primary-50 border border-primary-100 rounded-2xl p-4">
          <h4 className="text-sm font-semibold text-primary-800 mb-3">New Requirement</h4>
          <div className="flex flex-wrap items-end gap-3">
            <div><label className="block text-xs text-slate-600 mb-1">Grade *</label>
              <select value={addForm.grade} onChange={e=>setAddForm(f=>({...f,grade:e.target.value}))} required className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400">
                <option value="">— Select grade —</option>{grades.map(g=><option key={g} value={g}>{g}</option>)}</select></div>
            <div><label className="block text-xs text-slate-600 mb-1">Subject *</label>
              <select value={addForm.subject_id} onChange={e=>setAddForm(f=>({...f,subject_id:e.target.value}))} required className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 min-w-[160px]">
                <option value="">— Select subject —</option>{subjects.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            <div><label className="block text-xs text-slate-600 mb-1">Periods/Week</label>
              <input type="number" min="1" max="20" value={addForm.periods_per_week} onChange={e=>setAddForm(f=>({...f,periods_per_week:e.target.value}))} className="w-24 border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"/></div>
            <label className="flex items-center gap-2 text-sm text-slate-700 pb-0.5 cursor-pointer"><input type="checkbox" checked={addForm.can_double} onChange={e=>setAddForm(f=>({...f,can_double:e.target.checked}))} className="rounded"/>Double periods allowed</label>
            <div className="flex gap-2">
              <button type="submit" disabled={saving} className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50">{saving?<Loader2 size={13} className="animate-spin"/>:<Check size={13}/>}Add</button>
              <button type="button" onClick={()=>setAddForm(null)} className="px-4 py-2 text-sm text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
            </div>
          </div>
        </form>
      )}
      {rows.length===0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200"><BookOpen size={36} className="mx-auto text-slate-300 mb-3"/><p className="text-slate-600 font-medium">No requirements configured</p>{isAdmin&&<p className="text-slate-400 text-sm mt-1">Add grade × subject period requirements for timetable generation.</p>}</div>
      ) : (
        <>
          {Object.entries(byGrade).sort(([a],[b])=>a.localeCompare(b)).map(([grade,gRows])=>(
            <div key={grade} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-2 bg-slate-50 border-b flex items-center justify-between"><h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Grade {grade}</h4><span className="text-xs text-slate-400">{gRows.reduce((s,r)=>s+(parseInt(r.periods_per_week)||0),0)} periods/week total</span></div>
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 font-semibold bg-slate-50/60"><tr><th className="px-4 py-2 text-left">Subject</th><th className="px-3 py-2 text-right">Periods/Week</th><th className="px-3 py-2 text-center">Double</th>{isAdmin&&<th className="px-3 py-2 w-10"/>}</tr></thead>
                <tbody className="divide-y divide-slate-50">
                  {gRows.map(row=>{ const ai=rows.indexOf(row); return (
                    <tr key={row.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2 font-medium text-slate-700">{row.subject_label||row.subject_name||'—'}</td>
                      <td className="px-3 py-2 text-right">{isAdmin?<input type="number" min="1" max="20" value={row.periods_per_week} onChange={e=>upd(ai,'periods_per_week',parseInt(e.target.value))} className="w-16 border border-slate-200 rounded-lg px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-primary-400"/>:<span className="font-semibold">{row.periods_per_week}</span>}</td>
                      <td className="px-3 py-2 text-center">{isAdmin?<input type="checkbox" checked={!!row.can_double} onChange={e=>upd(ai,'can_double',e.target.checked)} className="rounded"/>:row.can_double?<Check size={14} className="mx-auto text-emerald-500"/>:<X size={14} className="mx-auto text-slate-300"/>}</td>
                      {isAdmin&&<td className="px-3 py-2 text-center"><button onClick={()=>handleDelete(row.id)} disabled={deleting===row.id} className="p-1 rounded text-slate-300 hover:text-red-500">{deleting===row.id?<Loader2 size={14} className="animate-spin"/>:<Trash2 size={14}/>}</button></td>}
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          ))}
          {isAdmin&&<button onClick={saveAll} disabled={saving} className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50">{saving?<Loader2 size={15} className="animate-spin"/>:<Save size={15}/>}Save Changes</button>}
        </>
      )}
    </div>
  );
}

// ── Slot Modal ────────────────────────────────────────────────
function SlotModal({ slot, grade, section, day, period, periodLabel, yearId, subjects, teachers, rooms, onSave, onClose }) {
  const existing = slot?.id ? slot : null;
  const [form, setForm] = useState({
    subject_id:   existing?.subject_id   || '',
    subject_name: existing?.subject_name || '',
    teacher_id:   existing?.teacher_id   || '',
    room_id:      existing?.room_id      || '',
    slot_type:    existing?.slot_type    || 'regular',
  });
  const [saving,  setSaving]  = useState(false);
  const [clearing,setClearing]= useState(false);
  const [conflict, setConflict] = useState(null);
  const set = (k,v) => { setForm(f=>({...f,[k]:v})); setConflict(null); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setConflict(null);
    try {
      const subj = subjects.find(s => String(s.id) === String(form.subject_id));
      await timetableAPI.upsertSlot({
        academicYearId: yearId, grade, section: section||'',
        day_of_week: day, period_number: period,
        subject_id:   form.subject_id   ? parseInt(form.subject_id)  : null,
        subject_name: subj?.name || form.subject_name || null,
        teacher_id:   form.teacher_id   ? parseInt(form.teacher_id)  : null,
        room_id:      form.room_id      ? parseInt(form.room_id)      : null,
        slot_type: form.slot_type,
      });
      toast.success('Slot saved'); onSave();
    } catch(e) {
      const d = e.response?.data;
      if (d?.conflict) { setConflict(d.error); }
      else toast.error(d?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleClear = async () => {
    if (!slot?.id) return onClose();
    setClearing(true);
    try { await timetableAPI.deleteSlot(slot.id); toast.success('Slot cleared'); onSave(); }
    catch { toast.error('Clear failed'); } finally { setClearing(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="font-display font-bold text-slate-800 text-sm">
              Grade {grade}{section ? ` · ${section}` : ''} — {day} {periodLabel}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">Assign a subject and teacher to this slot</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={18}/></button>
        </div>
        <form onSubmit={handleSave} className="p-5 space-y-4">
          {conflict && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-sm text-red-700">
              <span className="font-semibold shrink-0">Conflict:</span>
              <span>{conflict}</span>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Subject</label>
            <select value={form.subject_id} onChange={e=>{set('subject_id',e.target.value); if(e.target.value) set('subject_name','');}}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400">
              <option value="">— Select from list —</option>
              {subjects.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {!form.subject_id && (
              <input value={form.subject_name} onChange={e=>set('subject_name',e.target.value)}
                placeholder="Or type subject name manually"
                className="w-full mt-1.5 border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400"/>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Teacher <span className="text-slate-400 font-normal">(optional)</span></label>
            <select value={form.teacher_id} onChange={e=>set('teacher_id',e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400">
              <option value="">— No teacher assigned —</option>
              {teachers.map(t=><option key={t.id} value={t.id}>{t.name}{t.designation?` (${t.designation})`:''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Room <span className="text-slate-400 font-normal">(optional)</span></label>
            <select value={form.room_id} onChange={e=>set('room_id',e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400">
              <option value="">— No room assigned —</option>
              {rooms.map(r=><option key={r.id} value={r.id}>{r.name}{r.room_code?` [${r.room_code}]`:''}</option>)}
            </select>
          </div>
          <div className="flex justify-between items-center pt-2">
            {slot?.id ? (
              <button type="button" onClick={handleClear} disabled={clearing}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 bg-red-50 rounded-xl hover:bg-red-100 disabled:opacity-50">
                {clearing?<Loader2 size={13} className="animate-spin"/>:<Trash2 size={13}/>}Clear slot
              </button>
            ) : <div/>}
            <div className="flex gap-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200">Cancel</button>
              <button type="submit" disabled={saving}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 disabled:opacity-50">
                {saving?<Loader2 size={14} className="animate-spin"/>:<Save size={14}/>}Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Grid Tab ──────────────────────────────────────────────────
// ── PDF Import Modal ──────────────────────────────────────────────
function ImportPdfModal({ yearId, onClose, onDone }) {
  const [step, setStep]             = useState(1); // 1=upload 2=preview 3=done
  const [loading, setLoading]       = useState(false);
  const [preview, setPreview]       = useState(null);
  const [result, setResult]         = useState(null);
  const [resetExisting, setReset]   = useState(false);
  const [dragOver, setDragOver]     = useState(false);

  async function handleFile(file) {
    if (!file || file.type !== 'application/pdf') { toast.error('Please select a PDF file'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      const res = await timetableAPI.parsePdf(fd);
      setPreview(res.data);
      setStep(2);
    } catch(e) {
      toast.error('Parse failed: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }

  async function handleApply() {
    setLoading(true);
    try {
      const res = await timetableAPI.applyImport({
        import_id: preview.import_id,
        academic_year_id: yearId,
        reset_existing: resetExisting,
      });
      setResult(res.data.stats);
      setStep(3);
      onDone?.();
    } catch(e) {
      toast.error('Import failed: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }

  const steps = ['Upload PDF', 'Preview', 'Done'];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2">
            <FileUp size={18} className="text-primary-500"/>
            <h2 className="font-semibold text-slate-800">Import Timetable from PDF</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"><X size={16}/></button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-0 px-6 pt-4 shrink-0">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div className="flex items-center gap-1.5">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors
                  ${step > i+1 ? 'bg-emerald-500 text-white' : step === i+1 ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                  {step > i+1 ? <Check size={12}/> : i+1}
                </div>
                <span className={`text-xs font-medium ${step===i+1?'text-primary-700':'text-slate-400'}`}>{s}</span>
              </div>
              {i < steps.length-1 && <div className={`flex-1 h-px mx-2 ${step > i+1 ? 'bg-emerald-400' : 'bg-slate-200'}`}/>}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* Step 1 — Upload */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Upload an aSc Timetables PDF (teacher-wise format). The system will extract all teacher schedules and assign them to timetable slots.</p>
              <div
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
                className={`border-2 border-dashed rounded-2xl p-10 text-center transition-colors cursor-pointer
                  ${dragOver ? 'border-primary-400 bg-primary-50' : 'border-slate-200 hover:border-primary-300 hover:bg-slate-50'}`}
                onClick={() => document.getElementById('pdf-file-input').click()}
              >
                {loading ? (
                  <><Loader2 size={32} className="mx-auto text-primary-400 animate-spin mb-3"/><p className="text-sm text-slate-500">Extracting timetable data…</p><p className="text-xs text-slate-400 mt-1">This may take 20–40 seconds</p></>
                ) : (
                  <><Upload size={32} className="mx-auto text-slate-300 mb-3"/><p className="text-sm font-medium text-slate-600">Click to upload or drag & drop</p><p className="text-xs text-slate-400 mt-1">PDF files only · Max 20 MB</p></>
                )}
              </div>
              <input id="pdf-file-input" type="file" accept=".pdf,application/pdf" className="hidden" onChange={e => handleFile(e.target.files[0])}/>
            </div>
          )}

          {/* Step 2 — Preview */}
          {step === 2 && preview && (
            <div className="space-y-4">
              {/* Summary chips */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Teachers Found',   value: preview.teachers_found,   color: 'emerald' },
                  { label: 'Teachers Missing',  value: preview.teachers_missing, color: preview.teachers_missing > 0 ? 'amber' : 'slate' },
                  { label: 'Periods to Assign', value: preview.total_periods,    color: 'primary' },
                ].map(c => (
                  <div key={c.label} className={`rounded-xl p-3 text-center bg-${c.color}-50 border border-${c.color}-100`}>
                    <div className={`text-2xl font-bold text-${c.color}-600`}>{c.value}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{c.label}</div>
                  </div>
                ))}
              </div>

              {/* Missing teachers warning */}
              {preview.missing_teachers?.length > 0 && (
                <div className="flex gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <AlertCircle size={16} className="text-amber-500 shrink-0 mt-0.5"/>
                  <div>
                    <p className="text-xs font-semibold text-amber-700">Not found in database — will be skipped:</p>
                    <p className="text-xs text-amber-600 mt-0.5">{preview.missing_teachers.join(', ')}</p>
                  </div>
                </div>
              )}

              {/* Per-teacher preview table */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 border-b border-slate-200">
                  <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Teachers to Import ({preview.teachers_found})</span>
                </div>
                <div className="overflow-y-auto max-h-52">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-100 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-slate-500">#</th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-500">Teacher</th>
                        <th className="px-3 py-2 text-center font-semibold text-slate-500">Periods</th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-500">Classes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.map((t, i) => (
                        <tr key={t.teacher_id} className="border-b border-slate-50 hover:bg-slate-50/60">
                          <td className="px-3 py-1.5 text-slate-400">{i+1}</td>
                          <td className="px-3 py-1.5 font-medium text-slate-700">{t.teacher_name}</td>
                          <td className="px-3 py-1.5 text-center"><span className="bg-primary-100 text-primary-700 font-semibold px-2 py-0.5 rounded-full">{t.periods}</span></td>
                          <td className="px-3 py-1.5 text-slate-500 truncate max-w-[200px]">{t.classes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Reset option */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={resetExisting} onChange={e => setReset(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-primary-600"/>
                <span className="text-sm text-slate-600">Reset all existing teacher assignments before importing</span>
              </label>
            </div>
          )}

          {/* Step 3 — Done */}
          {step === 3 && result && (
            <div className="space-y-4 text-center py-4">
              <CheckCircle2 size={48} className="mx-auto text-emerald-500"/>
              <h3 className="font-semibold text-slate-800 text-lg">Import Complete</h3>
              <div className="grid grid-cols-2 gap-3 text-left max-w-sm mx-auto">
                {[
                  ['Teachers processed', result.pages],
                  ['Primary slots assigned', result.updated],
                  ['Co-teacher slots', result.coteach],
                  ['Skipped (conflicts)', result.skipped_dup],
                ].map(([l, v]) => (
                  <div key={l} className="bg-slate-50 rounded-xl p-3">
                    <div className="text-xl font-bold text-slate-700">{v}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 shrink-0">
          {step === 1 && <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>}
          {step === 2 && (
            <>
              <button onClick={() => setStep(1)} className="px-4 py-2 text-sm rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50">Back</button>
              <button onClick={handleApply} disabled={loading}
                className="px-5 py-2 text-sm rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-60 flex items-center gap-2">
                {loading ? <><Loader2 size={14} className="animate-spin"/>Importing…</> : 'Confirm Import'}
              </button>
            </>
          )}
          {step === 3 && <button onClick={onClose} className="px-5 py-2 text-sm rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700">Close</button>}
        </div>
      </div>
    </div>
  );
}

function GridTab({ yearId, isAdmin }) {
  const [view,       setView]       = useState('class');   // 'class' | 'teacher'
  const [classes,    setClasses]    = useState([]);
  const [teachers,   setTeachers]   = useState([]);
  const [subjects,   setSubjects]   = useState([]);
  const [rooms,      setRooms]      = useState([]);
  const [periods,    setPeriods]    = useState([]);
  const [workDays,   setWorkDays]   = useState(DAYS);
  const [selGrade,   setSelGrade]   = useState('');
  const [selSection, setSelSection] = useState('');
  const [selTeacher, setSelTeacher] = useState('');
  const [slots,      setSlots]      = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [modal,      setModal]      = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [metaLoading,  setMetaLoading]  = useState(true);

  // Load all reference data once
  useEffect(() => {
    async function loadMeta() {
      try {
        const [clR, stR, subR, rmR, prR, cfR] = await Promise.all([
          schoolConfigAPI.getClasses(),
          schoolConfigAPI.getStaff({ limit: 500 }),
          schoolConfigAPI.getSubjects(),
          timetableAPI.getRooms({ academicYearId: yearId }),
          timetableAPI.getPeriods({ academicYearId: yearId }),
          timetableAPI.getConfig({ academicYearId: yearId }),
        ]);
        setClasses(clR.data || []);
        setTeachers((stR.data || []).filter(t => t.is_active !== 0 && (t.staff_category === 'teaching' || !t.staff_category)));
        setSubjects((subR.data || []).filter(s => s.is_active !== 0));
        setRooms((rmR.data?.data || []).filter(r => r.is_active));
        setPeriods(prR.data?.data || []);
        const days = cfR.data?.working_days || DAYS;
        setWorkDays(Array.isArray(days) ? days : DAYS);
      } catch { toast.error('Failed to load timetable data'); }
      finally { setMetaLoading(false); }
    }
    loadMeta();
  }, [yearId]);

  // Derive unique grades from classes
  const gradeOptions = [...new Set(classes.map(c => c.grade))].sort();

  // Sections for selected grade
  const sectionOptions = selGrade
    ? [...new Set(classes.filter(c => c.grade === selGrade).map(c => c.section || ''))]
    : [];
  const hasMultipleSections = sectionOptions.length > 1;

  // For single-section grades, auto-use the stored section (avoids '' vs 'A' mismatch in DB).
  // For multi-section grades, use the selected section ('' = All Sections → no section filter).
  const effectiveSection = hasMultipleSections ? selSection : (sectionOptions[0] || '');

  // Load slots when grade/teacher selection changes
  useEffect(() => {
    async function loadSlots() {
      if (view === 'class' && !selGrade) { setSlots([]); setCompliance([]); return; }
      if (view === 'teacher' && !selTeacher) { setSlots([]); return; }
      setSlotsLoading(true);
      try {
        const params = { academicYearId: yearId };
        if (view === 'class') {
          params.grade = selGrade;
          // only add section filter when we have a specific section; omit to show all
          if (effectiveSection) params.section = effectiveSection;
        }
        if (view === 'teacher') { params.teacherId = selTeacher; }
        const sR = await timetableAPI.getSlots(params);
        setSlots(sR.data?.data || []);
        if (view === 'class' && selGrade) {
          const cR = await timetableAPI.getCompliance({ academicYearId: yearId, grade: selGrade, section: effectiveSection || '' });
          setCompliance(cR.data?.data || []);
        } else { setCompliance([]); }
      } catch { toast.error('Failed to load slots'); }
      finally { setSlotsLoading(false); }
    }
    loadSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearId, view, selGrade, selSection, selTeacher]);

  // Build slot map: map[day][period] = slot
  const slotMap = {};
  for (const s of slots) {
    if (!slotMap[s.day_of_week]) slotMap[s.day_of_week] = {};
    slotMap[s.day_of_week][s.period_number] = s;
  }

  const openModal = (day, period, slot) => {
    if (!isAdmin && view === 'class') return;
    setModal({ day, period, slot: slot || null });
  };

  const handleSaved = async () => {
    setModal(null);
    // Reload slots
    if (view === 'class' && selGrade) {
      setSlotsLoading(true);
      try {
        const params = { academicYearId: yearId, grade: selGrade };
        if (effectiveSection) params.section = effectiveSection;
        const [sR, cR] = await Promise.all([
          timetableAPI.getSlots(params),
          timetableAPI.getCompliance({ academicYearId: yearId, grade: selGrade, section: effectiveSection || '' }),
        ]);
        setSlots(sR.data?.data || []);
        setCompliance(cR.data?.data || []);
      } finally { setSlotsLoading(false); }
    } else if (view === 'teacher' && selTeacher) {
      setSlotsLoading(true);
      try {
        const sR = await timetableAPI.getSlots({ academicYearId: yearId, teacherId: selTeacher });
        setSlots(sR.data?.data || []);
      } finally { setSlotsLoading(false); }
    }
  };

  const getPeriodLabel = (num) => {
    const p = periods.find(p => p.period_number === num);
    return p?.label || `P${num}`;
  };
  const getPeriodTime = (num) => {
    const p = periods.find(p => p.period_number === num);
    return p ? `${(p.start_time||'').slice(0,5)}–${(p.end_time||'').slice(0,5)}` : '';
  };

  // All period numbers sorted by start_time so breaks appear in chronological order
  const periodNums = periods.length > 0
    ? [...periods].sort((a,b)=>(a.start_time||'').localeCompare(b.start_time||'')).map(p=>p.period_number)
    : [1,2,3,4,5,6,7,8];

  // Days to show (intersection of working_days and available DAYS order)
  const activeDays = DAYS.filter(d => workDays.includes(d));

  if (metaLoading) return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-primary-500"/></div>;

  return (
    <div className="space-y-5">
      {/* View toggle + selectors */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Class / Teacher toggle */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
          {[['class','By Class'],['teacher','By Teacher']].map(([k,l])=>(
            <button key={k} onClick={()=>{setView(k);setSlots([]);}}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                ${view===k?'bg-white text-primary-700 shadow-sm':'text-slate-500 hover:text-slate-700'}`}>
              {l}
            </button>
          ))}
        </div>

        {view==='class' && (
          <>
            <select value={selGrade} onChange={e=>{setSelGrade(e.target.value);setSelSection('');}}
              className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 min-w-[130px]">
              <option value="">— Select Grade —</option>
              {gradeOptions.map(g=><option key={g} value={g}>Grade {g}</option>)}
            </select>
            {hasMultipleSections && (
              <select value={selSection} onChange={e=>setSelSection(e.target.value)}
                className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400">
                <option value="">All Sections</option>
                {sectionOptions.filter(s=>s).map(s=><option key={s} value={s}>Section {s}</option>)}
              </select>
            )}
          </>
        )}

        {view==='teacher' && (
          <select value={selTeacher} onChange={e=>setSelTeacher(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 min-w-[200px]">
            <option value="">— Select Teacher —</option>
            {teachers.map(t=><option key={t.id} value={t.id}>{t.name}{t.designation?` (${t.designation})`:''}</option>)}
          </select>
        )}

        {/* Import PDF button — admin only */}
        {isAdmin && (
          <button onClick={() => setShowImport(true)}
            className="ml-auto flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors shadow-sm">
            <FileUp size={15}/> Import PDF
          </button>
        )}
      </div>

      {showImport && (
        <ImportPdfModal
          yearId={yearId}
          onClose={() => setShowImport(false)}
          onDone={() => { setSlots([]); setShowImport(false); }}
        />
      )}

      {/* Empty state */}
      {!selGrade && view==='class' && (
        <div className="text-center py-16 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
          <LayoutGrid size={40} className="mx-auto text-slate-300 mb-3"/>
          <p className="text-slate-600 font-medium">Select a grade to view its timetable</p>
          {!periods.length && <p className="text-amber-600 text-xs mt-2">⚠ No period templates found — set up periods first</p>}
        </div>
      )}
      {!selTeacher && view==='teacher' && (
        <div className="text-center py-16 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
          <LayoutGrid size={40} className="mx-auto text-slate-300 mb-3"/>
          <p className="text-slate-600 font-medium">Select a teacher to view their schedule</p>
        </div>
      )}

      {/* Timetable grid */}
      {((view==='class'&&selGrade)||(view==='teacher'&&selTeacher)) && (
        <>
          {slotsLoading ? (
            <div className="flex justify-center py-12"><Loader2 size={22} className="animate-spin text-primary-500"/></div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
              <table className="w-full border-collapse" style={{minWidth: `${activeDays.length*160+140}px`}}>
                <thead>
                  <tr className="bg-slate-50">
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-500 border-b border-r border-slate-200 sticky left-0 bg-slate-50 z-10 min-w-[120px]">
                      Period
                    </th>
                    {activeDays.map(day=>(
                      <th key={day} className="px-2 py-3 text-center text-xs font-semibold text-slate-700 border-b border-r border-slate-200 min-w-[150px]">
                        {DAY_LABELS[day]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {periodNums.map(pNum=>{
                    const pt = periods.find(p=>p.period_number===pNum);
                    const isNonRegular = pt && pt.period_type !== 'regular';
                    return (
                      <tr key={pNum} className={isNonRegular?'bg-amber-50/40':'hover:bg-slate-50/60'}>
                        {/* Period label */}
                        <td className="px-3 py-2 border-b border-r border-slate-100 sticky left-0 bg-inherit z-10">
                          <div className="font-semibold text-xs text-slate-700">{pt?.label||`P${pNum}`}</div>
                          {pt && <div className="text-[10px] text-slate-400 mt-0.5">{(pt.start_time||'').slice(0,5)}–{(pt.end_time||'').slice(0,5)}</div>}
                          {isNonRegular && <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full mt-1 inline-block ${PERIOD_TYPE_COLORS[pt.period_type]}`}>{PERIOD_TYPE_LABELS[pt.period_type]}</span>}
                        </td>
                        {/* Day cells */}
                        {activeDays.map(day=>{
                          const slot    = slotMap[day]?.[pNum];
                          const subjName = slot?.subject_label || slot?.subject_name;
                          const hasSubj  = !!subjName;
                          const cls      = hasSubj ? subjectClass(subjName) : '';
                          return (
                            <td key={day} className="px-1.5 py-1.5 border-b border-r border-slate-100 align-top">
                              <button
                                onClick={()=>openModal(day,pNum,slot)}
                                disabled={isNonRegular && !slot?.id}
                                className={`w-full min-h-[60px] rounded-xl border text-left p-2 transition-all
                                  ${isNonRegular&&!slot?.id ? 'bg-slate-50 border-slate-100 cursor-default' :
                                    !hasSubj ? 'border-dashed border-slate-200 hover:border-primary-300 hover:bg-primary-50/40 cursor-pointer' :
                                    `${cls} border cursor-pointer hover:opacity-90 hover:shadow-sm`}`}
                              >
                                {!hasSubj && !isNonRegular && isAdmin && (
                                  <span className="text-xs text-slate-300 flex items-center gap-1"><Plus size={11}/>Assign</span>
                                )}
                                {hasSubj && (
                                  <>
                                    <div className="font-semibold text-[11px] leading-tight truncate">{subjName}</div>
                                    {view==='teacher' && slot.grade && (
                                      <div className="text-[10px] font-semibold opacity-80 truncate mt-0.5">
                                        Gr. {slot.grade}{slot.section ? ` (${slot.section})` : ''}
                                      </div>
                                    )}
                                    {view==='class' && slot.teacher_name && <div className="text-[10px] opacity-70 truncate mt-0.5">{slot.teacher_name.split(' ').slice(0,2).join(' ')}</div>}
                                    {slot.room_code && <div className="text-[10px] opacity-50 mt-0.5">{slot.room_code}</div>}
                                  </>
                                )}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Compliance panel — class view only */}
          {view==='class' && compliance.length>0 && (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-50 border-b">
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Weekly Period Compliance — Grade {selGrade}</h4>
              </div>
              <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {compliance.map(c=>(
                  <div key={c.id} className={`rounded-xl p-3 border ${c.gap===0?'bg-emerald-50 border-emerald-100':'bg-amber-50 border-amber-100'}`}>
                    <div className="text-xs font-semibold text-slate-700 truncate">{c.subject_name}</div>
                    <div className="flex items-center gap-1 mt-1.5">
                      <span className={`text-lg font-display font-bold ${c.gap===0?'text-emerald-600':'text-amber-600'}`}>{c.assigned}</span>
                      <span className="text-xs text-slate-400">/ {c.periods_per_week}</span>
                    </div>
                    {c.gap>0 && <div className="text-[10px] text-amber-600 mt-0.5">{c.gap} short</div>}
                    {c.gap===0 && <div className="text-[10px] text-emerald-600 mt-0.5">Complete</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Slot modal */}
      {modal && view==='class' && (
        <SlotModal
          slot={modal.slot}
          grade={selGrade} section={effectiveSection}
          day={modal.day} period={modal.period}
          periodLabel={getPeriodLabel(modal.period)}
          yearId={yearId}
          subjects={subjects} teachers={teachers} rooms={rooms}
          onSave={handleSaved}
          onClose={()=>setModal(null)}
        />
      )}
    </div>
  );
}

// ── DashboardTab ──────────────────────────────────────────────
function KpiCard({ icon: Icon, iconBg, label, name, value, unit, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</span>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${iconBg}`}>
          <Icon size={17} className="text-white"/>
        </div>
      </div>
      <div>
        <p className="text-lg font-bold text-slate-800 leading-tight truncate" title={name}>
          {name || <span className="text-slate-400 font-normal text-sm">No data</span>}
        </p>
        {value != null && (
          <p className="text-2xl font-extrabold text-slate-900 mt-0.5">
            {value}
            {unit && <span className="text-sm font-medium text-slate-400 ml-1">{unit}</span>}
          </p>
        )}
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

const STATUS_BADGE = {
  ok:         'bg-emerald-50 text-emerald-700 border border-emerald-100',
  overloaded: 'bg-rose-50 text-rose-700 border border-rose-100',
};

function DashboardTab({ yearId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');

  useEffect(() => {
    setLoading(true);
    timetableAPI.getDashboard({ academicYearId: yearId })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [yearId]);

  const filtered = (data?.teachers || []).filter(t =>
    !search || t.teacher_name.toLowerCase().includes(search.toLowerCase()) ||
    (t.subjects_taught || '').toLowerCase().includes(search.toLowerCase())
  );

  const exportCSV = () => {
    const header = ['Teacher','Designation','Primary Subject','Periods/Week','Hours/Week','Classes','Subjects Taught','Max Periods/Day','Status'];
    const rows = (data?.teachers || []).map(t => [
      t.teacher_name, t.designation || '', t.primary_subject || '',
      t.periods_per_week, t.hours_per_week,
      t.classes_taught || '', t.subjects_taught || '',
      t.max_per_day, t.status,
    ]);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = 'teacher_workload.csv';
    a.click();
  };

  if (loading) return (
    <div className="flex items-center justify-center py-24 text-slate-400">
      <Loader2 size={22} className="animate-spin mr-2"/> Loading dashboard…
    </div>
  );

  const { kpi } = data || {};

  const KPI_CARDS = [
    { label: 'Most Loaded Teacher',   icon: TrendingUp,   iconBg: 'bg-rose-500',     kpi: kpi?.mostLoaded,  unit: 'periods/wk' },
    { label: 'Least Loaded Teacher',  icon: TrendingDown,  iconBg: 'bg-emerald-500',  kpi: kpi?.leastLoaded, unit: 'periods/wk' },
    { label: 'Most Occupied Room',    icon: Building2,     iconBg: 'bg-blue-500',     kpi: kpi?.mostRoom,    unit: 'slots'      },
    { label: 'Highest Period Subject',icon: BookMarked,    iconBg: 'bg-violet-500',   kpi: kpi?.highestSub,  unit: 'periods'    },
    { label: 'Lowest Period Subject', icon: BookOpen,      iconBg: 'bg-amber-500',    kpi: kpi?.lowestSub,   unit: 'periods'    },
    { label: 'Peak Utilization Time', icon: Zap,           iconBg: 'bg-orange-500',   kpi: kpi?.peakTime,    unit: 'classes',
      sub: kpi?.peakTime?.start ? `Starts ${kpi.peakTime.start.slice(0,5)}` : undefined },
  ];

  return (
    <div className="space-y-8">

      {/* KPI cards */}
      <div>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
          <BarChart3 size={15}/> Key Metrics
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {KPI_CARDS.map(({ label, icon, iconBg, kpi: k, unit, sub }) => (
            <KpiCard key={label}
              icon={icon} iconBg={iconBg} label={label}
              name={k?.name} value={k?.value} unit={unit}
              sub={sub || (k?.value != null ? undefined : 'No data yet')}
            />
          ))}
        </div>
      </div>

      {/* Teacher workload table */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-2">
            <TrendingUp size={15}/> Teacher Workload — Observation Summary
          </h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search teacher / subject…"
                className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-400 w-52"/>
            </div>
            <button onClick={exportCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition">
              <Download size={14}/> Export
            </button>
          </div>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-slate-100 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide border-b border-slate-100">
                <th className="text-left px-4 py-3 font-semibold">#</th>
                <th className="text-left px-4 py-3 font-semibold">Teacher</th>
                <th className="text-left px-4 py-3 font-semibold">Designation</th>
                <th className="text-left px-4 py-3 font-semibold">Primary Subject</th>
                <th className="text-center px-4 py-3 font-semibold">Periods / Week</th>
                <th className="text-center px-4 py-3 font-semibold">Hours / Week</th>
                <th className="text-center px-4 py-3 font-semibold">Max / Day</th>
                <th className="text-left px-4 py-3 font-semibold">Subjects Taught</th>
                <th className="text-left px-4 py-3 font-semibold">Classes</th>
                <th className="text-center px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="text-center py-12 text-slate-400">No data found</td></tr>
              )}
              {filtered.map((t, i) => (
                <tr key={t.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-slate-400 text-xs">{i + 1}</td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-slate-800">{t.teacher_name}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{t.designation || '—'}</td>
                  <td className="px-4 py-3">
                    {t.primary_subject
                      ? <span className="inline-flex px-2 py-0.5 rounded-lg bg-primary-50 text-primary-700 text-xs font-medium">{t.primary_subject}</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-blue-50 text-blue-700 font-bold text-sm">
                      {t.periods_per_week}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-semibold text-slate-700">{t.hours_per_week}h</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`font-semibold text-sm ${t.max_per_day > 6 ? 'text-rose-600' : 'text-slate-600'}`}>
                      {t.max_per_day}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs max-w-48 truncate" title={t.subjects_taught}>
                    {t.subjects_taught || '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs max-w-40 truncate" title={t.classes_taught}>
                    {t.classes_taught || '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-lg text-xs font-semibold capitalize ${STATUS_BADGE[t.status] || STATUS_BADGE.ok}`}>
                      {t.status === 'overloaded' ? 'Overloaded' : 'Normal'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          {filtered.length} teacher{filtered.length !== 1 ? 's' : ''} shown · 1 period = 40 min · Max 6 periods/day threshold
        </p>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────
export default function Timetable() {
  const { user }   = useAuth();
  const { yearId } = useContext(YearContext);
  const isAdmin    = ['admin','super_admin'].includes(user?.role);

  const tabs = [
    { key:'dashboard', label:'Dashboard',         icon:BarChart3   },
    { key:'rooms',     label:'Rooms',             icon:DoorOpen    },
    { key:'config',    label:'Config',            icon:Settings2   },
    { key:'periods',   label:'Period Builder',    icon:Clock       },
    { key:'grades',    label:'Grade Requirements',icon:BookOpen    },
    { key:'grid',      label:'Timetable Grid',    icon:LayoutGrid  },
  ];
  const [activeTab, setActiveTab] = useState('dashboard');

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <CalendarDays size={24} className="text-primary-600"/> Timetable
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Configure rooms, school hours, period structure, grade requirements, and assign slots
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 bg-slate-100 p-1.5 rounded-2xl w-fit">
        {tabs.map(({key,label,icon:Icon})=>(
          <button key={key} onClick={()=>setActiveTab(key)}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-medium transition whitespace-nowrap
              ${activeTab===key?'bg-white text-primary-700 shadow-sm':'text-slate-500 hover:text-slate-700 hover:bg-white/60'}`}>
            <Icon size={15}/> {label}
          </button>
        ))}
      </div>

      <div>
        {activeTab==='dashboard'&& <DashboardTab   yearId={yearId}/>}
        {activeTab==='rooms'   && <RoomsTab       yearId={yearId} isAdmin={isAdmin}/>}
        {activeTab==='config'  && <ConfigTab      yearId={yearId} isAdmin={isAdmin}/>}
        {activeTab==='periods' && <PeriodsTab     yearId={yearId} isAdmin={isAdmin}/>}
        {activeTab==='grades'  && <GradeConfigTab yearId={yearId} isAdmin={isAdmin}/>}
        {activeTab==='grid'    && <GridTab        yearId={yearId} isAdmin={isAdmin}/>}
      </div>
    </div>
  );
}
